/**
 * Asking the questions a dish comes with: what size, what extras.
 *
 * WHY THE TILL COULD NOT DO THIS
 * ------------------------------
 * There was nothing to ask. The cart has handled modifiers correctly all
 * along, and is tested — but no dish offered a single choice, because the
 * table joining a group to a dish had never held a row and no endpoint could
 * write one. The picker was missing at the bottom of a stack of missing
 * things.
 *
 * WHY IT CHECKS THE SAME RULE THE SERVER DOES
 * -------------------------------------------
 * The rule comes from `@foodrun/shared`, the same function the server calls.
 * Written twice it would drift, and the way it would drift is a screen that
 * lets a cashier finish an order the server then refuses — with a customer at
 * the counter, which is the worst possible moment to discover a disagreement.
 *
 * The server still checks. A till is a browser and anything it sends can be
 * edited; this one is here so the cashier is not told no after the fact.
 *
 * WHY THE RUNNING TOTAL IS ON SCREEN
 * ----------------------------------
 * Because it is the question the customer is asking. "How much with bacon" is
 * answered here or it is answered by surprise at the end.
 */
import { useState, useMemo } from 'react';
import { formatPKR, checkModifiers, fewestAllowed } from '@foodrun/shared';

import { Button } from '../../components/ui/Button.jsx';

export function ChoicePicker({
  product,
  groups,
  onCancel,
  onDone,
  // Inside a deal the item is not priced on its own — the deal is one fixed
  // price — so the standalone price and the choice upcharges are hidden and the
  // button says what it is really doing.
  hidePrices = false,
  confirmLabel = 'Add to the order',
  // Options to start ticked (their ids). Used to pre-fill a deal item's saved
  // default so it opens on what was chosen, not on the product's own default.
  initial = null,
}) {
  const [picked, setPicked] = useState(() => {
    // A saved selection wins; otherwise the group defaults, where it has one —
    // a required size that opens with nothing selected makes someone answer a
    // question the kitchen already has an answer to.
    if (initial && initial.length) return [...initial];
    const start = [];
    for (const group of groups) {
      const fallback = (group.options ?? []).find((o) => o.is_default);
      if (fallback) start.push(fallback.id);
    }
    return start;
  });

  const chosen = useMemo(() => picked.map((optionId) => ({ optionId })), [picked]);

  // The same check the server will run, so the two cannot disagree.
  const verdict = useMemo(
    () => checkModifiers(product, groups, chosen),
    [product, groups, chosen]
  );

  const price = Number(product.price) || 0;
  const total = price + (verdict.total ?? 0);

  function choose(group, optionId) {
    const max = Number(group.max_selections) || 0;
    const inGroup = (group.options ?? []).map((o) => o.id);

    setPicked((current) => {
      if (current.includes(optionId)) {
        /**
         * Tapping the chosen one in a pick-exactly-one group is not a way to
         * clear it — there is no valid state with nothing selected, so it
         * would only produce an error the cashier has to undo.
         */
        const floor = fewestAllowed(group);
        const remaining = current.filter((id) => inGroup.includes(id)).length - 1;
        if (remaining < floor) return current;

        return current.filter((id) => id !== optionId);
      }

      // In a pick-one group, choosing replaces rather than adds. Making the
      // cashier deselect first is a tap nobody needs at a counter.
      if (max === 1) {
        return [...current.filter((id) => !inGroup.includes(id)), optionId];
      }

      return [...current, optionId];
    });
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Choices for ${product.name}`}
      className="fixed inset-0 z-20 flex items-end justify-center bg-black/70 sm:items-center"
    >
      <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-t-2xl bg-surface p-5 sm:rounded-2xl">
        <h2 className="font-medium text-ink text-lg">{product.name}</h2>
        {hidePrices ? (
          <p className="mb-4 text-sm text-ink-3">Choose for this one</p>
        ) : (
          <p className="mb-4 text-sm text-ink-3">{formatPKR(price)} before choices</p>
        )}

        {groups.map((group) => {
          const max = Number(group.max_selections) || 0;
          const single = max === 1;
          const inGroup = (group.options ?? []).map((o) => o.id);
          const countHere = picked.filter((id) => inGroup.includes(id)).length;

          return (
            <fieldset key={group.id} className="mb-5">
              <legend className="mb-2 flex w-full items-baseline justify-between gap-3">
                <span className="text-sm font-medium text-ink">{group.name}</span>
                <span className="text-xs text-ink-3">
                  {/*
                    Said as an instruction rather than as the rule. "Choose 1"
                    is what somebody does; "min 1 max 1 required" is what a
                    database says.
                  */}
                  {fewestAllowed(group) > 0
                    ? single
                      ? 'Choose one'
                      : `Choose at least ${fewestAllowed(group)}`
                    : single
                      ? 'Optional'
                      : max > 0
                        ? `Up to ${max}`
                        : 'Optional'}
                </span>
              </legend>

              <div className="space-y-1.5">
                {(group.options ?? []).map((option) => {
                  const on = picked.includes(option.id);
                  const adjustment = Number(option.price_adjustment) || 0;
                  // Full in a multi-pick group, so the ones that would be
                  // refused read as unavailable rather than failing on tap.
                  const full = !on && !single && max > 0 && countHere >= max;

                  return (
                    <label
                      key={option.id}
                      className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 ${
                        on ? 'border-brand-400 bg-surface-3' : 'border-rule bg-surface-3'
                      } ${full ? 'opacity-40' : 'cursor-pointer'}`}
                    >
                      <input
                        type={single ? 'radio' : 'checkbox'}
                        name={group.id}
                        aria-label={option.name}
                        checked={on}
                        disabled={full}
                        onChange={() => choose(group, option.id)}
                        className="h-4 w-4"
                      />
                      <span className="flex-1 text-sm text-ink">{option.name}</span>
                      <span
                        className={`text-sm tabular-nums ${
                          adjustment < 0 ? 'text-warn' : 'text-ink-2'
                        }`}
                      >
                        {hidePrices || adjustment === 0
                          ? ''
                          : adjustment < 0
                            ? `−${formatPKR(-adjustment)}`
                            : `+${formatPKR(adjustment)}`}
                      </span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
          );
        })}

        {verdict.error && (
          <p role="alert" className="mb-3 rounded-lg bg-warn-bg px-3.5 py-2.5 text-sm text-warn">
            {verdict.error}
          </p>
        )}

        {hidePrices ? null : (
          <div className="mb-4 flex items-baseline justify-between border-t border-rule pt-3">
            <span className="text-sm text-ink-2">With these choices</span>
            <span className="text-xl font-semibold tabular-nums text-ink">{formatPKR(total)}</span>
          </div>
        )}

        <div className={`flex gap-2 ${hidePrices ? 'mt-4' : ''}`}>
          <Button variant="secondary" size="lg" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            size="lg"
            className="flex-1"
            disabled={Boolean(verdict.error)}
            onClick={() => onDone(verdict.modifiers)}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
